/* eslint-disable no-mixed-spaces-and-tabs */
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { requestShiftAction } from "../../../../../../export/shift-swaps";
import { FLEX_SCHEDULER_PERMISSIONS } from "../../../../../config/permissions";
import Button from "../../../../../design/button";
import LoadingButton from "../../../../../design/wilya-loading-button";
import { hasPermissionString } from "../../../../../utils/helpers";
import useSchedulerData from "../../../controllers/use-scheduler-data";

type ActionButtonsProps = {
  handleClose: (() => void) | undefined;
  isValid: boolean;
  dirty: boolean;
  isSubmitting: boolean;
  isShiftSwap?: boolean;
  isDisabled?: boolean;
  availableWorkers: number;
  jobSelected: boolean;
  skillSelected: boolean;
  decisionCode?: string;
  requestStatusCode?: string;
};

const ActionButtons: React.FC<ActionButtonsProps> = ({
  handleClose,
  isValid,
  dirty,
  isSubmitting,
  isShiftSwap,
  isDisabled,
  availableWorkers,
  jobSelected,
  skillSelected,
  decisionCode,
  requestStatusCode,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isRejecting, setIsRejecting] = useState(false);
  const {
    permissions,
    requestActionStatus,
    formData,
  } = useSchedulerData();

  const isAlreadyApproved =
    requestStatusCode === "a" || requestStatusCode === "aa";
  const isAlreadyDeclined = requestStatusCode === "r";

  const handleReject = () => {
    setIsRejecting(true);
    const payload = {
      ...formData,
      decisionCode: "r",
      flexShiftDetails: null,
    };
    dispatch(requestShiftAction(payload));
  };

  React.useEffect(() => {
    if (requestActionStatus === "fulfilled" && isRejecting) {
      setIsRejecting(false);
      handleClose && handleClose();
    }
  }, [requestActionStatus, isRejecting, handleClose]);

  const isRejectLoading = isRejecting && requestActionStatus === "pending";

  return (
    <Box display="flex" width="100%" alignItems="center" flexDirection="column">
      <Box
        sx={{
          width: "100%",
        }}
      >
        {isShiftSwap &&
        availableWorkers === 0 &&
        (jobSelected || skillSelected) ? (
          <Box
            sx={{
              display: "flex",
              padding: "12px",
              flexDirection: "row",
              backgroundColor: "#FBEAEA",
              borderRadius: "4px",
              marginBottom: "16px",
              marginTop: "16px",
            }}
          >
            <ErrorOutlineOutlinedIcon sx={{ color: "#E44B48" }} />
            <Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#541313",
                }}
              >
                {t("scheduler_no-eligible-workers")}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          <Button color="primary" variant="text" onClick={handleClose}>
            {t("common_cancel")}
          </Button>
          {isShiftSwap && !isAlreadyDeclined && (
            <LoadingButton
              variant="outlined"
              sx={{
                marginLeft: "16px",
                color: "#d32f2f",
                borderColor: "#d32f2f",
                "&:hover": {
                  borderColor: "#d32f2f",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
              loading={isRejectLoading}
              onClick={handleReject}
              loadingIndicator={
                <CircularProgress size={16} sx={{ color: "#d32f2f" }} />
              }
              disabled={
                requestActionStatus === "pending" ||
                isSubmitting ||
                (isAlreadyApproved && !decisionCode)
              }
            >
              {t("scheduler_decline")}
            </LoadingButton>
          )}
          <Button
            color="primary"
            type="submit"
            customStyles={{
              marginLeft: "16px",
            }}
            disabled={
              !hasPermissionString(
                permissions,
                FLEX_SCHEDULER_PERMISSIONS.CREATE_SHIFT
              )
                ? true
                : !isValid ||
                  !dirty ||
                  isSubmitting ||
                  isDisabled ||
                  (isShiftSwap &&
                    availableWorkers === 0 &&
                    (jobSelected || skillSelected))
            }
          >
            {isSubmitting ? (
              <div style={{ position: "relative" }}>
                <CircularProgress
                  size={25}
                  style={{ position: "absolute", left: "50%" }}
                />{" "}
                {isShiftSwap
                  ? t("scheduler_approving")
                  : t("scheduler_creating-shift")}
              </div>
            ) : isShiftSwap ? (
              isAlreadyApproved ? (
                t("scheduler_post-coverage")
              ) : (
                t("scheduler_approve")
              )
            ) : (
              t("scheduler_create-shift")
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ActionButtons;
